import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  listReportAttachments, uploadReportAttachment, updateReportAttachment,
  deleteReportAttachment, reportAttachmentViewUrl, reportAttachmentDownloadUrl,
} from "../../lib/api";
import { FileGlyph, humanSize, IMG_EXT } from "./AttachmentPicker";
import { Paperclip, FolderOpen, Camera, Eye, Download, Trash2, Pencil, Check, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.heic,.heif,.docx,.xlsx,.doc,.xls";
const MAX_BYTES = 25 * 1024 * 1024;

/**
 * Live attachment manager for an existing report. Shows thumbnails / file icons,
 * uploader + date, inline preview (image/PDF), description editing, and (admins
 * only) soft-delete with a required reason.
 */
export default function ReportAttachments({ reportType, reportId, isAdmin, canManage = true, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDesc, setEditDesc] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await listReportAttachments(reportType, reportId);
      setItems(data || []);
    } catch { /* report may not be visible */ }
    setLoading(false);
  }, [reportType, reportId]);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > MAX_BYTES) { toast.error(`"${f.name}" exceeds 25 MB`); continue; }
      try {
        await uploadReportAttachment(reportType, reportId, f, "");
      } catch (e) {
        toast.error(e.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    toast.success("Attachment(s) uploaded");
    load();
  };

  const saveDesc = async (att) => {
    try {
      await updateReportAttachment(att.id, editDesc);
      setEditingId(null);
      load();
    } catch { toast.error("Failed to update description"); }
  };

  const confirmDelete = async () => {
    if (!deleteReason.trim()) { toast.error("A reason is required"); return; }
    try {
      await deleteReportAttachment(deleteTarget.id, deleteReason.trim());
      toast.success("Attachment deleted");
      setDeleteTarget(null); setDeleteReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-3" data-testid="report-attachments">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <Paperclip className="w-4 h-4" /> Attachments {items.length > 0 && <span className="text-gray-400 font-normal">({items.length})</span>}
      </div>

      {loading ? (
        <div className="h-8 bg-gray-100 rounded animate-pulse" />
      ) : items.length === 0 ? (
        <p className="text-xs text-gray-400">No attachments.</p>
      ) : (
        <div className={compact ? "space-y-2" : "grid grid-cols-1 sm:grid-cols-2 gap-2"}>
          {items.map((a) => (
            <div key={a.id} className="flex items-start gap-3 p-2 bg-white border rounded-lg" data-testid={`attachment-${a.id}`}>
              {IMG_EXT.test(a.filename) ? (
                <a href={reportAttachmentViewUrl(a.id)} target="_blank" rel="noreferrer" title="Preview">
                  <img src={reportAttachmentViewUrl(a.id)} alt={a.filename} className="w-14 h-14 object-cover rounded border" />
                </a>
              ) : (
                <div className="w-14 h-14 flex items-center justify-center rounded bg-gray-100 text-gray-500 border">
                  <FileGlyph name={a.filename} className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" title={a.filename}>{a.filename}</p>
                {editingId === a.id ? (
                  <div className="flex items-center gap-1 mt-1">
                    <input value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
                      className="flex-1 text-xs border rounded px-2 py-1" placeholder="Description"
                      data-testid={`attachment-desc-input-${a.id}`} />
                    <button onClick={() => saveDesc(a)} className="text-green-600 p-1" data-testid={`attachment-desc-save-${a.id}`}><Check className="w-4 h-4" /></button>
                    <button onClick={() => setEditingId(null)} className="text-gray-400 p-1"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  a.description
                    ? <p className="text-xs text-gray-600 italic">"{a.description}"</p>
                    : <p className="text-xs text-gray-300 italic">No description</p>
                )}
                <p className="text-[11px] text-gray-400 mt-0.5">{a.uploaded_by_name} · {fmtDate(a.uploaded_at)} · {humanSize(a.size)}</p>
                <div className="flex items-center gap-2 mt-1">
                  <a href={reportAttachmentViewUrl(a.id)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] text-[#00305E] hover:underline" data-testid={`attachment-view-${a.id}`}>
                    <Eye className="w-3.5 h-3.5" />Preview
                  </a>
                  <a href={reportAttachmentDownloadUrl(a.id)}
                    className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:underline" data-testid={`attachment-download-${a.id}`}>
                    <Download className="w-3.5 h-3.5" />Download
                  </a>
                  {canManage && (
                    <button onClick={() => { setEditingId(a.id); setEditDesc(a.description || ""); }}
                      className="inline-flex items-center gap-1 text-[11px] text-gray-600 hover:underline" data-testid={`attachment-edit-${a.id}`}>
                      <Pencil className="w-3.5 h-3.5" />Edit
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => { setDeleteTarget(a); setDeleteReason(""); }}
                      className="inline-flex items-center gap-1 text-[11px] text-red-500 hover:underline" data-testid={`attachment-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />Delete
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); }}
          className={`rounded-lg border-2 border-dashed p-3 text-center transition-colors ${dragging ? "border-[#00305E] bg-[#00305E]/5" : "border-gray-200"}`}
        >
          <div className="flex flex-wrap gap-2 justify-center items-center">
            <span className="text-xs text-gray-500">{uploading ? "Uploading…" : "Add attachment:"}</span>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="attachment-add-browse"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-[#00305E] text-white hover:bg-[#00305E]/90 disabled:opacity-50">
              <FolderOpen className="w-3.5 h-3.5" />Browse
            </button>
            <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="attachment-add-camera"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50">
              <Camera className="w-3.5 h-3.5" />Photo
            </button>
          </div>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden"
            onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} />
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteReason(""); } }}>
        <AlertDialogContent data-testid="attachment-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete attachment</AlertDialogTitle>
            <AlertDialogDescription>
              A reason is required and logged for compliance. The file is removed from view but retained in the audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <p className="text-sm font-medium truncate">{deleteTarget?.filename}</p>
            <textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} rows={3}
              placeholder="Reason for deletion (required)" className="w-full text-sm border rounded px-2 py-1.5"
              data-testid="attachment-delete-reason" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              className="bg-red-600 hover:bg-red-700" data-testid="attachment-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
